import React from 'react';
import { Text, Logo } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { GridCell, Grid, Page } from 'styles';
import { LineItems } from 'pages/billing/components/line-items';
import { getServicePeriod } from 'domain/statement';
import { formatDate } from '@perch-labs/warbler';
import {
  LineItemsTableHead,
  LineItemsTableBody,
  RecipientTableHead,
  RecipientTableBody,
} from './printed-statement.style';

const getCopy = copyFor('billing.statement.printedStatement');

export type PrintedStatementProps = {
  lineItems: FormattedLineItem[];
  paymentDate: string;
  paymentMethodDescription?: string;
  total: number;
  user: {
    email: string;
    name: string;
  };
  utilityStatements: {
    id: number;
    serviceStartDate: string;
    serviceEndDate: string;
    utilityAccount: {
      accountNumber: string | null;
      displayServiceAddress: string;
      utility: {
        displayName: string;
      };
    };
  }[];
};

// ref required https://github.com/gregnb/react-to-print
export const PrintedStatement = React.forwardRef(
  (props: PrintedStatementProps, ref) => {
    const {
      lineItems,
      paymentDate,
      paymentMethodDescription,
      total,
      user,
      utilityStatements,
    } = props;
    const firstUtilityAccount = utilityStatements[0]?.utilityAccount;
    const firstServiceAddress = firstUtilityAccount?.displayServiceAddress;
    const firstUtilityName = firstUtilityAccount?.utility.displayName;

    return (
      <Page surface padding={{ bottom: '24px', top: '24px' }} ref={ref}>
        <Logo variant="default" />
        <Grid gap="24px" margin={{ top: '24px' }}>
          <GridCell columns={6}>
            <Text textStyle="heading400">{getCopy('sender.name')}</Text>
            <Text textStyle="paragraph400">{getCopy('sender.street')}</Text>
            <Text textStyle="paragraph400">{getCopy('sender.streetTwo')}</Text>
            <Text textStyle="paragraph400">
              {getCopy('sender.cityStateZip')}
            </Text>
          </GridCell>
          <GridCell columns={6} justifySelf="right" textAlign="right">
            <Text textStyle="heading400">{getCopy('accountInfo')}</Text>
            <Text textStyle="paragraph400">{user.name}</Text>
            <Text textStyle="paragraph400">{user.email}</Text>
            {firstServiceAddress && (
              <Text textStyle="paragraph400">{firstServiceAddress}</Text>
            )}
          </GridCell>
          <GridCell columns={12} margin={{ top: '16px' }}>
            <Text tag="h2" textStyle="heading800">
              {getCopy('title', { email: user.email })}
            </Text>
          </GridCell>
          {utilityStatements.length > 0 && (
            <GridCell columns={12}>
              <RecipientTableHead>
                <Text textStyle="heading400">
                  {getCopy('overview.servicePeriod')}
                </Text>
                <Text textStyle="heading400">
                  {getCopy('overview.paymentDate')}
                </Text>
                <Text textStyle="heading400">
                  {getCopy('overview.paymentMethod')}
                </Text>
                <Text textStyle="heading400">
                  {getCopy('overview.utilityAcccount')}
                </Text>
              </RecipientTableHead>
              {utilityStatements.map(us => (
                <RecipientTableBody key={us.id}>
                  <Text textStyle="paragraph400">{getServicePeriod(us)}</Text>
                  <Text textStyle="paragraph400">
                    {formatDate(paymentDate, { format: 'MMMM d, yyyy' })}
                  </Text>
                  <Text textStyle="paragraph400">
                    {paymentMethodDescription ?? ''}
                  </Text>
                  <Text textStyle="paragraph400">
                    {us.utilityAccount.accountNumber || ''}
                  </Text>
                </RecipientTableBody>
              ))}
            </GridCell>
          )}
          <GridCell columns={12}>
            <LineItemsTableHead>
              <Text textStyle="heading400">{getCopy('lineItems.product')}</Text>
              <Text textStyle="heading400">{getCopy('lineItems.amount')}</Text>
            </LineItemsTableHead>
            <LineItemsTableBody>
              <LineItems
                includeTooltips={false}
                includeSupportingText={true}
                lineItemsFormatted={lineItems}
                statementTotal={total}
                utilityName={firstUtilityName}
              />
            </LineItemsTableBody>
          </GridCell>
        </Grid>
      </Page>
    );
  }
);
